import { Col, DatePicker, Input, Row } from "antd";
import Button from "devextreme-react/button";
import moment, { Moment } from "moment";
import { FC, useState } from "react";
import FontFa from "../../components/fontFa/fontFa";
import { DateFormat } from "../../constants";
import SelectRoute from "../../pages/procedure/company-office/business-tickets-v2/components/select-route";
import "./businessForm.scss";
import { IInputProps } from "./types";

type FormField = {
  from: string;
  to: string;
  date: string;
};

const BusinessFormComponent: FC<IInputProps> = ({
  onChange,
  onTouched,
  value,
  name,
  inputRef,
  isDisabled,
  isPassport,
}) => {
  const today = moment().toLocaleString();
  const [formData, setFormData] = useState<FormField[]>(
    value.length > 0 ? [...value] : [{ from: "", to: "", date: today }],
  );
  const [lastChangedIndex, setLastChangedIndex] = useState<number | null>(null);

  const handleAddRow = () => {
    const lastRow = formData[formData.length - 1];
    const lastDate = moment(lastRow.date);
    const newDate = lastDate.clone().add(1, "day").toLocaleString();
    setFormData([...formData, { from: "", to: "", date: newDate }]);
    setLastChangedIndex(null);
  };

  const handleChange = (event: any, index: number, field: keyof FormField, stringValue: string | Moment) => {
    const updatedFormData = [...formData];
    if (field === "date") {
      const updatedDate = moment(stringValue).toLocaleString();
      updatedFormData[index][field] = updatedDate;
      setLastChangedIndex(index);
    } else {
      updatedFormData[index][field] = stringValue as string;
    }
    setFormData(updatedFormData);
    onChange(updatedFormData);
  };

  const handleTabKey = (e: any) => {
    if (e.keyCode === 13) handleAddRow();
  };

  const handleDeleteRow = (index: number) => {
    const updatedFormData = [...formData];
    updatedFormData.splice(index, 1);
    setFormData(updatedFormData);
    setLastChangedIndex(null);
  };

  const isDateDuplicate = (index: number): boolean => {
    // Chỉ hiển thị border đỏ cho row vừa được thay đổi nếu nó trùng với row khác
    if (lastChangedIndex !== index) return false;

    const currentDate = moment(formData[index].date);
    return formData.some((row, idx) => {
      if (idx === index) return false;
      const otherDate = moment(row.date);
      return currentDate.format("YYYY-MM-DD") === otherDate.format("YYYY-MM-DD");
    });
  };

  const isDateBeforeOrEqualPrev = (index: number): boolean => {
    if (index === 0) return false;
    const currentDate = moment(formData[index].date);
    const prevDate = moment(formData[index - 1].date);
    return currentDate.isSameOrBefore(prevDate, "day");
  };

  const shouldDisableDate = (current: Moment | null, index: number): boolean => {
    if (!current || index === 0) return false;
    const prevDate = moment(formData[index - 1].date);
    return current.isSameOrBefore(prevDate, "day");
  };

  const getDatePickerClass = (index: number) => {
    const hasOrderError = isDateBeforeOrEqualPrev(index);
    const hasDuplicateError = isDateDuplicate(index);
    return `form-input form-input--date-picker ${
      hasOrderError || hasDuplicateError ? "form-input--date-picker-error" : ""
    }`;
  };

  return (
    <div className="form-container">
      {formData.map((row, index) => {
        return isPassport ? (
          <div className="flex items-center gap-2 mx-[13px] my-2">
            <Row gutter={8} className="flex-1">
              <Col span={8}>
                <SelectRoute
                  name="from"
                  onTouched={() => {}}
                  value={row.from || undefined}
                  onChange={(e) => handleChange(e, index, "from", e)}
                  placeholder="Nơi đi (Mã sân bay)"
                />
              </Col>
              <Col span={8}>
                <DatePicker
                  style={{ width: "50%" }}
                  showTime={{ format: "HH:mm" }}
                  format={DateFormat.DD_MM_YYYY_HH_MM}
                  disabledDate={(current) => shouldDisableDate(current, index)}
                  onChange={(e) => handleChange(null, index, "date", e!)}
                  className={getDatePickerClass(index)}
                  value={moment(row.date)}
                />
              </Col>
              <Col span={8}>
                <Input
                  value={row.to}
                  onChange={(e) => handleChange(e, index, "to", e.target.value)}
                  placeholder="Đơn vị công tác"
                  className="form-input"
                  onKeyDown={handleTabKey}
                />
              </Col>
            </Row>
            <Button onClick={() => handleDeleteRow(index)} className="delete-button">
              <FontFa name="faTrash" className="text-danger" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 mx-[13px] my-2">
            <Row gutter={8} className="flex-1">
              <Col span={8}>
                <SelectRoute
                  name="from"
                  onTouched={() => {}}
                  value={row.from || undefined}
                  onChange={(e) => handleChange(e, index, "from", e)}
                  placeholder="Nơi đi (Mã sân bay)"
                />
              </Col>
              <Col span={8}>
                <SelectRoute
                  name="to"
                  onTouched={() => {}}
                  value={row.to || undefined}
                  onChange={(e) => handleChange(e, index, "to", e)}
                  placeholder="Nơi đến (Mã sân bay)"
                />
              </Col>
              <Col span={8}>
                <DatePicker
                  showTime={{ format: "HH:mm" }}
                  format={DateFormat.DD_MM_YYYY_HH_MM}
                  disabledDate={(current) => shouldDisableDate(current, index)}
                  onChange={(e) => handleChange(null, index, "date", e!)}
                  className={getDatePickerClass(index)}
                  value={moment(row.date)}
                  allowClear={false}
                />
              </Col>
            </Row>
            <Button onClick={() => handleDeleteRow(index)} className="delete-button">
              <FontFa name="faTrash" className="text-danger" />
            </Button>
          </div>
        );
      })}
      <Button onClick={handleAddRow} className="add-row-button">
        🔰 Thêm hành trình
      </Button>
    </div>
  );
};

// Export hàm kiểm tra trùng ngày để sử dụng bên ngoài
export const checkDuplicateDates = (data: FormField[]): boolean => {
  const dateMap = new Map<string, number[]>();
  data.forEach((row, index) => {
    if (row.date) {
      const dateKey = moment(row.date).format("YYYY-MM-DD");
      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, []);
      }
      dateMap.get(dateKey)!.push(index);
    }
  });
  return Array.from(dateMap.values()).some((indices) => indices.length > 1);
};

export default BusinessFormComponent;
